import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaEllipsisV, FaFileAlt, FaPaperclip } from 'react-icons/fa';
import { graphqlRequest } from '../../utils/graphqlRequest';
import {
  REPORTED_ISSUES_QUERY,
  REPORTED_ISSUE_QUERY,
  REPORTED_ISSUE_MESSAGES_QUERY,
  CREATE_REPORTED_ISSUE_MESSAGE_MUTATION,
  UPDATE_REPORTED_ISSUE_MUTATION,
} from '../../graphql/reportedIssue';
import { ADMIN_ROUTES, adminPath } from '../../constants/adminPortal';
import {
  formatDateTime,
  issueIdLabel,
  issueTypeLabel,
  normalizeRole,
  priorityBadge,
  priorityToFormValue,
  roleBadgeClass,
  roleLabel,
  statusBadge,
  statusToFormValue,
} from '../../utils/reportedIssueDisplay';

const BASE = adminPath(ADMIN_ROUTES.SUPPORT_MANAGEMENT);

const CREATE_MY_PROFILE_NOTE = `
  mutation CreateMyProfileNote($input: CreateUserProfileNoteInput!) {
    createMyProfileNote(input: $input) {
      id
      body
      tag
      createdAt
      authorName
    }
  }
`;

export default function SupportReportedIssueDetail() {
  const { issueId } = useParams();
  const navigate = useNavigate();

  const [issue, setIssue] = useState(null);
  const [allIssues, setAllIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);

  const [formStatus, setFormStatus] = useState('NEW');
  const [formPriority, setFormPriority] = useState('MEDIUM');
  const [notifyUser, setNotifyUser] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [noteSuccess, setNoteSuccess] = useState('');

  const load = useCallback(async () => {
    if (!issueId) return;
    setLoading(true);
    setLoadError('');
    try {
      const [one, listData] = await Promise.all([
        graphqlRequest(REPORTED_ISSUE_QUERY, { id: issueId }),
        graphqlRequest(REPORTED_ISSUES_QUERY),
      ]);
      const row = one?.reportedIssue;
      if (!row) {
        setLoadError('Issue not found.');
        setIssue(null);
      } else {
        setIssue(row);
        setFormStatus(statusToFormValue(row.status));
        setFormPriority(priorityToFormValue(row.priority));
      }
      setAllIssues(Array.isArray(listData?.reportedIssues) ? listData.reportedIssues : []);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load issue');
      setIssue(null);
      setAllIssues([]);
    } finally {
      setLoading(false);
    }
  }, [issueId]);

  const loadMessages = useCallback(async () => {
    if (!issueId) return;
    setMessagesLoading(true);
    try {
      const data = await graphqlRequest(REPORTED_ISSUE_MESSAGES_QUERY, { issueId });
      setMessages(Array.isArray(data?.reportedIssueMessages) ? data.reportedIssueMessages : []);
    } catch {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [issueId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const relatedIssues = useMemo(() => {
    if (!issue?.submittedByEmail) return [];
    const email = String(issue.submittedByEmail).toLowerCase();
    return allIssues
      .filter(
        (r) =>
          r.id !== issue.id &&
          String(r.submittedByEmail || '').toLowerCase() === email,
      )
      .slice(0, 5);
  }, [allIssues, issue]);

  const reporterStats = useMemo(() => {
    if (!issue?.submittedByEmail) return { total: 1, memberSince: issue?.createdAt };
    const email = String(issue.submittedByEmail).toLowerCase();
    const same = allIssues.filter(
      (r) => String(r.submittedByEmail || '').toLowerCase() === email,
    );
    const dates = same.map((r) => new Date(r.createdAt).getTime()).filter(Number.isFinite);
    const earliest = dates.length ? new Date(Math.min(...dates)) : null;
    return { total: same.length, memberSince: earliest?.toISOString() || issue?.createdAt };
  }, [allIssues, issue]);

  const handleSaveSidebar = async () => {
    if (!issue) return;
    setSaveError('');
    setSaving(true);
    try {
      await graphqlRequest(UPDATE_REPORTED_ISSUE_MUTATION, {
        input: {
          id: issue.id,
          status: formStatus,
          priority: formPriority,
        },
      });
      await load();
    } catch (e) {
      setSaveError(e?.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async () => {
    if (!issue) return;
    setSaveError('');
    setSaving(true);
    try {
      await graphqlRequest(UPDATE_REPORTED_ISSUE_MUTATION, {
        input: { id: issue.id, status: 'RESOLVED' },
      });
      setFormStatus('RESOLVED');
      await load();
    } catch (e) {
      setSaveError(e?.message || 'Could not resolve issue.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReply = async () => {
    const content = String(replyText || '').trim();
    if (!issue?.id || !content) return;
    setSendingMessage(true);
    try {
      await graphqlRequest(CREATE_REPORTED_ISSUE_MESSAGE_MUTATION, {
        input: {
          issueId: issue.id,
          content,
        },
      });
      setReplyText('');
      await loadMessages();
    } catch (e) {
      setSaveError(e?.message || 'Could not send reply.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSaveAdminNote = async () => {
    const body = String(adminNote || '').trim();
    if (!body) {
      setNoteError('Note cannot be empty.');
      return;
    }
    setNoteSaving(true);
    setNoteError('');
    setNoteSuccess('');
    try {
      await graphqlRequest(CREATE_MY_PROFILE_NOTE, {
        input: { body, tag: 'Info', reportedIssueId: issue.id },
      });
      setAdminNote('');
      setNoteSuccess(
        'Note saved. You and other Admin or Super Admin users can get an in-app toast.error(with Notes communication enabled).',
      );
      window.dispatchEvent(new Event('admin-backend-notifications-refresh'));
    } catch (e) {
      setNoteError(e?.message || 'Could not save note.');
    } finally {
      setNoteSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ri-detail">
        <p className="smgmt-muted">Loading issue…</p>
      </div>
    );
  }

  if (loadError || !issue) {
    return (
      <div className="ri-detail">
        <Link to={`${BASE}/reported-issues`} className="ri-detail-back">
          <FaArrowLeft aria-hidden /> Back to reported issues
        </Link>
        <p style={{ color: '#b91c1c', marginTop: 16 }}>{loadError || 'Issue not found.'}</p>
      </div>
    );
  }

  const role = normalizeRole(issue.submittedByRole);
  const attachments = Array.isArray(issue.attachmentFileNames) ? issue.attachmentFileNames : [];

  return (
    <div className="ri-detail">
      <Link to={`${BASE}/reported-issues`} className="ri-detail-back">
        <FaArrowLeft aria-hidden /> Back to reported issues
      </Link>

      <div className="ri-detail-grid">
        <div className="ri-detail-main">
          <div className="ri-detail-card ri-detail-header-card">
            <div className="ri-detail-header-top">
              <div>
                <h1 className="ri-detail-title">{issue.title}</h1>
                <div className="ri-detail-meta">
                  <span className="ri-detail-meta-item">
                    <strong>{issueIdLabel(issue)}</strong>
                  </span>
                  <span className="ri-detail-meta-sep">·</span>
                  <span>{issue.submittedByName || issue.submittedByEmail || '—'}</span>
                  <span className={roleBadgeClass(role)} style={{ marginLeft: 8 }}>
                    {roleLabel(role)}
                  </span>
                  <span className="ri-detail-meta-sep">·</span>
                  <span className="smgmt-muted">{formatDateTime(issue.createdAt)}</span>
                </div>
                <div className="ri-detail-badges-row">
                  {statusBadge(issue.status)}
                  {priorityBadge(issue.priority)}
                </div>
                <p className="ri-detail-context">
                  {issue.relatedProject?.trim() ? (
                    <>
                      <span className="smgmt-muted">Related project:</span> {issue.relatedProject}
                    </>
                  ) : (
                    <span className="smgmt-muted">No related project</span>
                  )}
                  <span className="ri-detail-meta-sep"> · </span>
                  <span className="smgmt-muted">Issue type:</span> {issueTypeLabel(issue.issueType)}
                </p>
              </div>
              <button type="button" className="smgmt-icon-btn" aria-label="More actions">
                <FaEllipsisV />
              </button>
            </div>
            <div className="ri-detail-actions">
              <button type="button" className="smgmt-btn" disabled title="Coming soon">
                Assign Admin
              </button>
              <select
                className="ri-detail-select"
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value)}
                aria-label="Change status"
              >
                <option value="NEW">New</option>
                <option value="UNDER_REVIEW">Under review</option>
                <option value="RESOLVED">Resolved</option>
              </select>
              <button
                type="button"
                className="smgmt-btn smgmt-btn-green"
                onClick={handleResolve}
                disabled={saving || formStatus === 'RESOLVED'}
              >
                Resolve Issue
              </button>
            </div>
          </div>

          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Issue description</h2>
            <div className="ri-detail-description">{issue.description}</div>
            <p className="ri-detail-note smgmt-muted">
              Device, app version, and platform are not collected in the current form. Add them to
              the report form if you need them stored.
            </p>
          </div>

          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Attachments</h2>
            {attachments.length === 0 ? (
              <p className="smgmt-muted">No attachments.</p>
            ) : (
              <ul className="ri-detail-attachments">
                {attachments.map((name) => (
                  <li key={name} className="ri-detail-attachment-row">
                    <span className="ri-detail-file-icon" aria-hidden>
                      <FaFileAlt />
                    </span>
                    <span className="ri-detail-file-name">{name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Conversation with user</h2>
            {messagesLoading ? (
              <p className="smgmt-muted ri-detail-empty-thread">Loading messages…</p>
            ) : messages.length === 0 ? (
              <p className="smgmt-muted ri-detail-empty-thread">No messages yet.</p>
            ) : (
              <ul className="ri-detail-messages">
                {messages.map((m) => {
                  const roleKey = String(m.senderRole || '').toLowerCase();
                  const isAdmin = roleKey === 'manager' || roleKey === 'admin';
                  return (
                    <li
                      key={m.id}
                      className={`ri-detail-message-item${isAdmin ? ' ri-detail-message-item--admin' : ''}`}
                    >
                      <div className="ri-detail-message-author">
                        {m.senderName || (isAdmin ? 'Admin' : 'User')}
                      </div>
                      <div className="ri-detail-message-content">{m.content}</div>
                      <div className="ri-detail-message-time smgmt-muted">{formatDateTime(m.createdAt)}</div>
                    </li>
                  );
                })}
              </ul>
            )}
            <label className="ri-detail-reply-label">
              <span className="smgmt-muted">Write a response to the user…</span>
              <textarea
                className="ri-detail-reply-input"
                rows={4}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type a reply…"
              />
            </label>
            <div className="ri-detail-reply-footer">
              <button type="button" className="ri-detail-attach-link" disabled>
                <FaPaperclip aria-hidden /> Attach file
              </button>
              <button
                type="button"
                className="smgmt-btn smgmt-btn-blue"
                onClick={handleSendReply}
                disabled={sendingMessage || !String(replyText || '').trim()}
              >
                {sendingMessage ? 'Sending…' : 'Send Reply'}
              </button>
            </div>
          </div>

          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Note to admin</h2>
            <label className="ri-detail-reply-label">
              <span className="smgmt-muted">Internal note (visible to admins only)</span>
              <textarea
                className="ri-detail-reply-input"
                rows={4}
                value={adminNote}
                onChange={(e) => {
                  setAdminNote(e.target.value);
                  setNoteSuccess('');
                }}
                placeholder="Add context or handoff notes for your team..."
              />
            </label>
            {noteError ? <p className="ri-detail-save-error">{noteError}</p> : null}
            {noteSuccess ? (
              <p className="smgmt-muted" style={{ marginTop: 8, color: '#15803d' }}>
                {noteSuccess}
              </p>
            ) : null}
            <div className="ri-detail-reply-footer">
              <span className="smgmt-muted">Saves to Admin Profile → Notes</span>
              <button
                type="button"
                className="smgmt-btn smgmt-btn-blue"
                onClick={handleSaveAdminNote}
                disabled={noteSaving || !String(adminNote || '').trim()}
              >
                {noteSaving ? 'Saving…' : 'Add note'}
              </button>
            </div>
          </div>
        </div>

        <aside className="ri-detail-sidebar">
          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Reporter information</h2>
            <dl className="ri-detail-dl">
              <dt>Name</dt>
              <dd>{issue.submittedByName || '—'}</dd>
              <dt>Email</dt>
              <dd>{issue.submittedByEmail || '—'}</dd>
              <dt>Phone</dt>
              <dd>—</dd>
              <dt>Total reports</dt>
              <dd>{reporterStats.total}</dd>
              <dt>Member since</dt>
              <dd>{reporterStats.memberSince ? formatDateTime(reporterStats.memberSince) : '—'}</dd>
            </dl>
          </div>

          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Ticket management</h2>
            <label className="ri-detail-field">
              <span>Status</span>
              <select
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value)}
              >
                <option value="NEW">New</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </label>
            <label className="ri-detail-field">
              <span>Priority</span>
              <select
                value={formPriority}
                onChange={(e) => setFormPriority(e.target.value)}
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </label>
            <label className="ri-detail-field">
              <span>Assigned admin</span>
              <select disabled>
                <option>—</option>
              </select>
            </label>
            <label className="ri-detail-field">
              <span>Department</span>
              <select disabled>
                <option>—</option>
              </select>
            </label>
            <label className="ri-detail-check">
              <input
                type="checkbox"
                checked={notifyUser}
                onChange={(e) => setNotifyUser(e.target.checked)}
              />
              Notify user about updates
            </label>
            <label className="ri-detail-check">
              <input type="checkbox" disabled />
              Escalate to engineering team
            </label>
            {saveError ? (
              <p className="ri-detail-save-error">{saveError}</p>
            ) : null}
            <button
              type="button"
              className="smgmt-btn smgmt-btn-blue ri-detail-save"
              onClick={handleSaveSidebar}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Activity</h2>
            <ul className="ri-detail-timeline">
              <li>
                <span className="ri-detail-timeline-dot ri-detail-timeline-dot--new" />
                <div>
                  <strong>Issue reported</strong>
                  <p className="smgmt-muted">{formatDateTime(issue.createdAt)}</p>
                </div>
              </li>
              {attachments.length > 0 ? (
                <li>
                  <span className="ri-detail-timeline-dot ri-detail-timeline-dot--file" />
                  <div>
                    <strong>Attachment{attachments.length > 1 ? 's' : ''} included</strong>
                    <p className="smgmt-muted">{formatDateTime(issue.createdAt)}</p>
                  </div>
                </li>
              ) : null}
            </ul>
          </div>

          <div className="ri-detail-card">
            <h2 className="ri-detail-section-title">Related reports</h2>
            {relatedIssues.length === 0 ? (
              <p className="smgmt-muted">No other issues from this reporter.</p>
            ) : (
              <ul className="ri-detail-related">
                {relatedIssues.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="ri-detail-related-link"
                      onClick={() => navigate(`${BASE}/reported-issues/${r.id}`)}
                    >
                      <strong>{issueIdLabel(r)}</strong>
                      <span className="ri-detail-related-title">{r.title}</span>
                      {statusBadge(r.status)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
